import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import api, { formatApiError } from "../lib/api";
import { generateAuditPdf } from "../lib/pdf";

const TIER_META = {
  Critical: { color: "#EF4444", desc: "Immediate, structured intervention recommended." },
  Exposed: { color: "#F59E0B", desc: "Several wellness gaps that compound risk over time." },
  Performing: { color: "#10B981", desc: "Solid foundations — fine-tune for compounding gains." },
  Elite: { color: "#06B6D4", desc: "Top-percentile habits. Maintain, mentor, optimise edges." },
};

export default function Audit() {
  const { slug } = useParams();
  const [stage, setStage] = useState("loading"); // loading | gate | intro | questions | submit | result | error
  const [audit, setAudit] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  // Per-question answers
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);

  // Submission
  const [submitForm, setSubmitForm] = useState({ name: "", email: "", phone: "", department: "", opt_in: false });
  const [auditStartedAt, setAuditStartedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api
      .get(`/audit/${slug}`)
      .then((r) => {
        setAudit(r.data);
        setStage("gate");
      })
      .catch((e) => {
        setErrMsg(formatApiError(e));
        setStage("error");
      });
  }, [slug]);

  const questions = audit?.questions || [];
  const total = questions.length;
  const progress = total ? ((idx + 1) / total) * 100 : 0;
  const currentAnswer = questions[idx] ? answers[questions[idx].id] : null;

  const setAnswer = (val) => {
    if (!questions[idx]) return;
    setAnswers((a) => ({ ...a, [questions[idx].id]: val }));
    setTimeout(() => {
      if (idx < total - 1) setIdx(idx + 1);
      else setStage("submit");
    }, 220);
  };

  const goBack = () => idx > 0 && setIdx(idx - 1);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: submitForm.name,
        email: submitForm.email,
        phone: submitForm.phone || null,
        department: submitForm.department || null,
        opt_in: submitForm.opt_in,
        consent_medical: true,
        answers,
      };
      const { data } = await api.post(`/audit/${slug}/submit`, payload);
      setResult({ ...data, name: submitForm.name });
      setStage("result");
    } catch (e2) {
      setErrMsg(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReport = () => {
    if (!result) return;
    generateAuditPdf({
      name: result.name,
      score: result.score,
      tier: result.tier,
      ownerName: audit?.owner_name,
      ownerCompany: audit?.owner_company,
      answers,
      questions,
    });
  };

  // ------ Render ------
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="audit-page">
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(700px 400px at 20% 10%, rgba(0, 191, 255, 0.18), transparent 60%), radial-gradient(700px 500px at 90% 90%, rgba(37, 99, 235, 0.18), transparent 60%)",
        }}
      />
      <div className="relative max-w-2xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="audit-home-link">
          <ArrowLeft className="w-4 h-4" /> fitest.co.uk
        </Link>

        {stage === "loading" && (
          <div className="mt-24 text-center text-slate-400" data-testid="audit-loading">Loading audit…</div>
        )}

        {stage === "error" && (
          <div className="mt-24 glass rounded-2xl p-8 text-center" data-testid="audit-error">
            <p className="text-2xl font-display mb-2">Audit unavailable</p>
            <p className="text-slate-400 text-sm">{errMsg}</p>
          </div>
        )}

        {stage === "gate" && (
          <Gate
            ownerName={audit?.owner_name}
            ownerCompany={audit?.owner_company}
            onAccept={() => setStage("intro")}
          />
        )}

        {stage === "intro" && (
          <Intro
            ownerName={audit?.owner_name}
            ownerCompany={audit?.owner_company}
            total={total}
            onStart={() => {
              setAuditStartedAt(Date.now());
              setStage("questions");
            }}
          />
        )}

        {stage === "questions" && questions[idx] && (
          <QuestionStage
            q={questions[idx]}
            idx={idx}
            total={total}
            progress={progress}
            current={currentAnswer}
            onPick={setAnswer}
            onBack={goBack}
          />
        )}

        {stage === "submit" && (
          <SubmitForm
            form={submitForm}
            setForm={setSubmitForm}
            onSubmit={onSubmit}
            submitting={submitting}
            error={errMsg}
            onBack={() => {
              setIdx(total - 1);
              setStage("questions");
            }}
          />
        )}

        {stage === "result" && result && (
          <ResultStage result={result} downloadReport={downloadReport} answers={answers} questions={questions} />
        )}
      </div>
    </div>
  );
}

function Gate({ ownerName, ownerCompany, onAccept }) {
  const [accepted, setAccepted] = useState(false);
  return (
    <div className="mt-8 glass-dark rounded-3xl p-8 sm:p-10" data-testid="audit-gate">
      <span className="eyebrow">Medical Disclaimer</span>
      <h1 className="font-display tracking-tight text-3xl sm:text-4xl mt-3">Before you start your wellness audit</h1>
      <p className="text-slate-300 mt-4 leading-relaxed text-sm">
        This audit, presented by{" "}
        <span className="text-white font-medium">{ownerCompany || ownerName || "Fitest"}</span>, is for informational
        and educational purposes only. It does not provide medical advice, diagnosis or treatment, and is not a
        substitute for professional medical guidance. By proceeding, you acknowledge that any actions you take based on
        these results are at your own discretion, and you should consult a qualified healthcare professional for any
        medical concerns. Fitest, Leadsopedia Limited, and the issuing licence holder accept no liability for outcomes
        arising from the use of this audit.
      </p>

      <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="audit-disclaimer-toggle">
        <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-1" />
        <span className="text-sm text-slate-300">I have read and accept the medical disclaimer.</span>
      </label>

      <Button
        disabled={!accepted}
        onClick={onAccept}
        className="btn-glow rounded-full h-12 px-7 mt-8 disabled:opacity-50"
        data-testid="audit-accept-btn"
      >
        Accept & Continue <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

function Intro({ ownerName, ownerCompany, total, onStart }) {
  return (
    <div className="mt-8 glass rounded-3xl p-8 sm:p-10" data-testid="audit-intro">
      <span className="eyebrow">Wellness Audit</span>
      <h1 className="font-display tracking-tighter text-4xl sm:text-5xl mt-3">
        {total} questions. Around 3 minutes.
      </h1>
      <p className="text-slate-300 mt-4 leading-relaxed">
        Powered by <span className="text-white font-medium">{ownerCompany || ownerName || "Fitest"}</span>. Swipe or
        tap your answer on a 1–5 scale, get scored 0–100, and download a personalised 3-page PDF report at the end.
      </p>
      <Button onClick={onStart} className="btn-glow rounded-full h-12 px-7 mt-8" data-testid="audit-start-btn">
        Start Audit <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

const SCALE = [
  { v: 1, label: "Strongly Disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neutral" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly Agree" },
];

function QuestionStage({ q, idx, total, progress, current, onPick, onBack }) {
  return (
    <div className="mt-6" data-testid="audit-question-stage">
      <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-3">
        <span>QUESTION {idx + 1} / {total}</span>
        <span className="text-brand">{q.category}</span>
      </div>
      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-[#2563EB] to-[#00BFFF]"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
          data-testid="audit-progress-bar"
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          className="mt-8 glass-dark rounded-3xl p-8 sm:p-10 min-h-[320px] flex flex-col"
          data-testid={`audit-question-${idx}`}
        >
          <p className="font-display tracking-tight text-2xl sm:text-3xl leading-snug">{q.text}</p>

          <div className="mt-auto pt-8 grid grid-cols-5 gap-2">
            {SCALE.map((s) => {
              const active = current === s.v;
              return (
                <button
                  key={s.v}
                  onClick={() => onPick(s.v)}
                  className={`group relative rounded-xl p-4 border transition-all ${
                    active
                      ? "border-[var(--border-active)] bg-[rgba(0,191,255,0.08)]"
                      : "border-white/10 hover:border-white/30 bg-white/[0.02]"
                  }`}
                  data-testid={`audit-answer-${s.v}`}
                  aria-label={`${s.v} - ${s.label}`}
                >
                  <span className={`font-display text-2xl block ${active ? "text-brand" : "text-white"}`}>
                    {s.v}
                  </span>
                  <span className="block text-[10px] text-slate-500 mt-1 leading-tight hidden sm:block">{s.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between items-center mt-6 text-sm">
        <button
          onClick={onBack}
          disabled={idx === 0}
          className="text-slate-400 hover:text-white disabled:opacity-30 inline-flex items-center gap-1.5"
          data-testid="audit-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-slate-500 hidden sm:block">Tap or swipe to continue →</div>
      </div>
    </div>
  );
}

function SubmitForm({ form, setForm, onSubmit, submitting, error, onBack }) {
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={onSubmit} className="mt-6 glass rounded-3xl p-8 sm:p-10" data-testid="audit-submit-form">
      <span className="eyebrow">Final step</span>
      <h2 className="font-display tracking-tight text-3xl mt-3">Where should we send your results?</h2>
      <p className="text-slate-400 text-sm mt-2">Your data is stored securely and never sold. You'll receive a personalised PDF.</p>

      <div className="mt-8 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-300">Name</Label>
          <Input required value={form.name} onChange={update("name")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="audit-form-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-300">Email</Label>
          <Input type="email" required value={form.email} onChange={update("email")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="audit-form-email" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-300">Phone (optional)</Label>
          <Input value={form.phone} onChange={update("phone")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="audit-form-phone" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-300">Department / Team (optional)</Label>
          <Input
            value={form.department}
            onChange={update("department")}
            placeholder="e.g. Sales · Engineering · Member Services"
            className="bg-white/5 border-white/10 mt-1 h-11"
            data-testid="audit-form-department"
          />
        </div>
        <label className="flex items-start gap-3 cursor-pointer mt-3" data-testid="audit-form-optin">
          <Checkbox checked={form.opt_in} onCheckedChange={(v) => setForm({ ...form, opt_in: !!v })} className="mt-1" />
          <span className="text-sm text-slate-300">
            I want support from my gym / employer based on my results.{" "}
            <span className="text-slate-500">(They will only see your contact details if you tick this.)</span>
          </span>
        </label>
      </div>

      {error && <p className="text-sm text-red-400 mt-4">{error}</p>}

      <div className="mt-8 flex gap-3">
        <Button type="button" onClick={onBack} className="btn-ghost-bordered rounded-full h-12 px-6" data-testid="audit-back-to-q">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button type="submit" disabled={submitting} className="btn-glow rounded-full h-12 px-7 flex-1" data-testid="audit-final-submit">
          {submitting ? "Scoring…" : "Get my results"} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

function ResultStage({ result, downloadReport }) {
  const meta = TIER_META[result.tier] || TIER_META.Performing;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-6"
      data-testid="audit-result"
    >
      <div className="glass-dark rounded-3xl p-8 sm:p-12 text-center">
        <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/10 mb-6">
          <Check className="w-7 h-7 text-brand" />
        </div>
        <span className="eyebrow">Your wellness score</span>
        <div className="font-display text-7xl sm:text-8xl mt-4 tracking-tighter" style={{ color: meta.color }} data-testid="audit-score">
          {result.score}
          <span className="text-3xl text-slate-500 ml-2">/100</span>
        </div>
        <div className="font-display text-2xl mt-3" data-testid="audit-tier">{result.tier}</div>
        <p className="text-slate-400 max-w-sm mx-auto mt-3 text-sm">{meta.desc}</p>

        <Button onClick={downloadReport} className="btn-glow rounded-full h-12 px-7 mt-10" data-testid="audit-download-pdf">
          <Download className="w-4 h-4 mr-2" /> Download my 3-page report
        </Button>

        <p className="text-xs text-slate-500 mt-8">
          Powered by Fitest — a trading name of Leadsopedia Limited.
        </p>
      </div>
    </motion.div>
  );
}
